import { get } from 'app/shared/utils/fetchr';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { FINCEN_SAR_QUERY_KEYS } from 'app/modules/fincenSAR/queries/keys';
import { useSelector, useDispatch } from 'react-redux';
import { selectHasReadSARsPermissions } from 'app/modules/session/selectors';
import {
  CachedSARFilingResponse,
  FullSARFilingResponse,
} from 'app/modules/fincenSAR/responses';
import { selectDLEnabledForSARDetail } from 'app/shared/featureFlags/selectors';
import { useEffect, useMemo, useRef } from 'react';
import { setLoadingRetrieveSar } from 'app/modules/fincenSAR/sliceFincenSar';
import { DataLoaderState } from 'app/modules/investigations/types/responses';
import {
  COLD_STORAGE_MAX_RETRIES,
  getColdStorageRetryWaitTime,
  getLoadingFromColdStorageState,
  hasAnyCGDOsLoading,
  isArticleLoadingResponse,
} from 'app/shared/utils/fastArchHelpers';

interface CachedSARFilingResponseWithDataLoader extends CachedSARFilingResponse {
  data_loader: DataLoaderState<'SAR_FILING'>;
}

type GetResponse =
  | CachedSARFilingResponseWithDataLoader
  | FullSARFilingResponse;

const isLoadingResponse = (
  data: GetResponse,
): data is CachedSARFilingResponseWithDataLoader => {
  return isArticleLoadingResponse(data, 'SAR_FILING');
};

export const useGetFinCENSAR = (
  id: number,
  options: Pick<UseQueryOptions, 'refetchOnWindowFocus'> = {},
) => {
  const hasReadSARsPermission = useSelector(selectHasReadSARsPermissions);
  const useSARCacheEndpoint = useSelector(selectDLEnabledForSARDetail);
  const dispatch = useDispatch();
  const dataUpdateCountRef = useRef(0);

  const query = useQuery<GetResponse>({
    ...options,
    staleTime: 1000, // Prevent refetching on the first load
    enabled: hasReadSARsPermission,
    queryKey: FINCEN_SAR_QUERY_KEYS.getFinCENSAR(id, useSARCacheEndpoint),
    queryFn: () =>
      useSARCacheEndpoint
        ? get<CachedSARFilingResponseWithDataLoader>(`/v1/sars/cached/${id}`)
        : get<FullSARFilingResponse>(`/v1/sars/${id}`),
    refetchInterval: ({ state: { data } }) => {
      if (useSARCacheEndpoint) {
        if (
          data &&
          isLoadingResponse(data) &&
          hasAnyCGDOsLoading(data.data_loader.article_ids[id]) &&
          dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
        ) {
          return getColdStorageRetryWaitTime(dataUpdateCountRef.current);
        }
      }
      // Poll when SAR is in content generation (missing CGDOs) until status changes
      if (
        data?.status === 'PREFILING_CONTENT_GENERATION' &&
        dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
      ) {
        return getColdStorageRetryWaitTime(dataUpdateCountRef.current);
      }
      // Do not retry by default
      return false;
    },
  });

  useEffect(() => {
    dispatch(setLoadingRetrieveSar(query.isLoading));
  }, [dispatch, query.isLoading]);

  const coldStorageLoading = useMemo(() => {
    return getLoadingFromColdStorageState(
      id,
      query.data && isLoadingResponse(query.data)
        ? query.data.data_loader
        : undefined,
    );
  }, [id, query.data]);

  useEffect(() => {
    // Reset update count when the ID changes
    dataUpdateCountRef.current = 0;
  }, [id]);

  // Increase the countRef once per update when we get a loading response or content-generation status
  useEffect(() => {
    if (
      query.data &&
      (isLoadingResponse(query.data) ||
        query.data.status === 'PREFILING_CONTENT_GENERATION')
    ) {
      dataUpdateCountRef.current += 1;
    }
  }, [query.data]);

  return {
    query,
    coldStorageLoading,
  };
};
